// SPDX-License-Identifier: GPL-3.0-or-later
// Shared names for the SigmaBots table (B3).
// The table data, the rules, the characters and the tests all read these names.
// This file has no imports and no DOM. The editor may import it.

/**
 * Item names in the table data (src/table/data/vibe-01.table.json).
 * The rules use these names to read events and to call the rules context.
 *
 * Input convention (a seam that the contracts do not cover):
 * The engine sends a player input to the rules as a PhysicsEvent with
 * `ballId` 0 and `type` "hit":
 * - a flipper button press: `kind` "flipper", `item` LeftFlipper or RightFlipper
 * - a tilt event (a nudge that is too hard): `kind` "decoration", `item` Tilt
 * A flipper event with a `ballId` above 0 is a ball contact. The rules ignore it.
 */
export const ITEM = {
  leftFlipper: "LeftFlipper",
  rightFlipper: "RightFlipper",
  upperFlipper: "UpperFlipper",
  bumper1: "Bumper1",
  bumper2: "Bumper2",
  bumper3: "Bumper3",
  slingLeft: "SlingLeft",
  slingRight: "SlingRight",
  saucer: "Saucer",
  kickback: "KickbackKicker",
  drain: "Drain",
  laneL: "LaneL",
  laneG: "LaneG",
  laneT: "LaneT",
  laneM: "LaneM",
  topAccept: "TopLaneAccept",
  topAll: "TopLaneAll",
  topYes: "TopLaneYes",
  shooter: "ShooterLane",
  orbitLeftTop: "OrbitLeftTop",
  orbitRightTop: "OrbitRightTop",
  rampLeftExit: "RampLeftExit",
  rampRightExit: "RampRightExit",
  spinner: "YoloSpinner",
  token1: "TokenTarget1",
  token2: "TokenTarget2",
  token3: "TokenTarget3",
  denyD: "DenyD",
  denyE: "DenyE",
  denyN: "DenyN",
  denyY: "DenyY",
  plunger: "Plunger",
  /** Tilt event name (see the input convention above). */
  tilt: "Tilt",
} as const;

/** Light names in the table data. The rules call setLight with these names. */
export const LIGHT = {
  laneL: "LaneLightL",
  laneG: "LaneLightG",
  laneT: "LaneLightT",
  laneM: "LaneLightM",
  kickback: "KickbackLamp",
  noGuard: "NoGuardLamp",
  denyD: "DenyLightD",
  denyE: "DenyLightE",
  denyN: "DenyLightN",
  denyY: "DenyLightY",
  topAccept: "TopLightAccept",
  topAll: "TopLightAll",
  topYes: "TopLightYes",
  token1: "TokenLight1",
  token2: "TokenLight2",
  token3: "TokenLight3",
  saucerMode: "SaucerModeLamp",
  saucerLock: "SaucerLockLamp",
  saucerWizard: "SaucerWizardLamp",
  saucerJackpot: "SaucerJackpotLamp",
  saucerArrow: "SaucerArrow",
  shotRampLeft: "ShotLampRampLeft",
  shotRampRight: "ShotLampRampRight",
  shotOrbitLeft: "ShotLampOrbitLeft",
  shotOrbitRight: "ShotLampOrbitRight",
  spinner: "SpinnerLamp",
  mode1: "ModeLamp1",
  mode2: "ModeLamp2",
  mode3: "ModeLamp3",
  mode4: "ModeLamp4",
  lock1: "LockLamp1",
  lock2: "LockLamp2",
  bonus2: "BonusLamp2",
  bonus3: "BonusLamp3",
  bonus4: "BonusLamp4",
  bonus5: "BonusLamp5",
  saver: "SaverLamp",
} as const;

/** Decoration names (DecorationSpec.name) of the eight characters. */
export const DECORATION = {
  dennis: "Dennis",
  rex: "Rex",
  haley: "Haley",
  carl: "Carl",
  randy: "Randy",
  larry: "Larry",
  tessa: "Tessa",
  prod: "Prod",
} as const;

/** Decoration registry ids (DecorationSpec.meshId and TableInfoSpec.playfieldArt). */
export const MESH_ID = {
  dennis: "char.dennis",
  rex: "char.rex",
  haley: "char.haley",
  carl: "char.carl",
  randy: "char.randy",
  larry: "char.larry",
  tessa: "char.tessa",
  prod: "char.prod",
  playfield: "art.playfield",
} as const;

/** Every id that the decoration registry (characters/index.ts) builds. */
export const KNOWN_MESH_IDS: readonly string[] = Object.values(MESH_ID);

/** Mode ids. The ids are the strings in `startMode`, `endMode` and `TableRules.modes`. */
export const MODE_ID = {
  acceptAll: "accept_all",
  worksOnMyMachine: "works_on_my_machine",
  contextWindowFull: "context_window_full",
  yoloFridayDeploy: "yolo_friday_deploy",
  agentSwarm: "agent_swarm",
  askMode: "ask_mode_production",
} as const;

export type ModeId = (typeof MODE_ID)[keyof typeof MODE_ID];

/** The four timed modes, in the order of the mode list. */
export const TIMED_MODE_IDS: readonly ModeId[] = [
  MODE_ID.acceptAll,
  MODE_ID.worksOnMyMachine,
  MODE_ID.contextWindowFull,
  MODE_ID.yoloFridayDeploy,
];

/**
 * Pseudo decoration names for `RulesContext.animate`.
 * The rules send text for the screen through two channels:
 * - CALLOUT_TARGET: a callout line or a message line (see callouts.ts)
 * - HUD_TARGET: a timer or a goal for the status strip (see callouts.ts)
 */
export const CALLOUT_TARGET = "Callout";
export const HUD_TARGET = "Hud";
